import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import Tab from "./Tab";
import styled from "styled-components";
import { useSwipeable } from "react-swipeable";

const TabLayoutContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const TabsContainer = styled.div`
	position: ${(props) => (props.isTopBar ? "sticky" : "relative")};
	top: ${(props) => (props.isTopBar ? "24px" : "0px")};
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	border-bottom: 1px solid var(--color-outline);
	background: var(
		${(props) =>
			props.isLifted
				? "--color-surface-container-highest"
				: "--color-background"}
	);
	transition: 0.3s;
	z-index: ${(props) => (props.isLifted ? "2" : "0")};
`;

const TabIndicator = styled.div`
	position: absolute;
	bottom: 0;
	height: 3px;
	background-color: var(--color-primary);
	border-top-left-radius: 3px;
	border-top-right-radius: 3px;
	transition: ease-in-out 0.3s;
`;

const ViewPagerContainer = styled.div`
	display: flex;
	overflow: hidden;
	width: 100%;
	position: relative;
`;

const ViewPager = styled.div`
	width: 100%;
	flex: 0 0 100%;
	transition: transform 0.3s ease;
`;

const TabLayout = forwardRef(
	({ tabs, isTopBar = false, onTabSelectedChange = () => {} }, ref) => {
		const [activeTab, setActiveTab] = useState(0);
		const [swipePosition, setSwipePosition] = useState(0);
		const [lastSwipePosition, setLastSwipePosition] = useState(0);
		const [lifted, setLifted] = useState(false);

		const activeTabRef = useRef(null);
		const indicatorRef = useRef(null);
		const viewPagerRef = useRef(null);

		const handleTabClick = (index) => {
			if (activeTab === index) return;

			onTabSelectedChange(index);
			setActiveTab(index);
			setSwipePosition(index * -100);
		};

		const handleSwipe = (eventData) => {
			const { deltaX, velocity } = eventData;
			const tabWidth = 100 / tabs.length;
			const newPotentialPosition =
				lastSwipePosition +
				(deltaX / window.innerWidth) * 100 * velocity;
			const newPotentialTab = Math.round(
				-newPotentialPosition / tabWidth
			);

			if (
				(activeTab === 0 && deltaX > 0 && newPotentialPosition > 0) || // Limite a sinistra
				(activeTab === tabs.length - 1 &&
					deltaX < 0 &&
					newPotentialPosition < activeTab * -100) // Limite a destra
			) {
				const dampenedDeltaX = deltaX * 0.2; // Riduci lo spostamento del 80%
				const dampenedNewPosition =
					lastSwipePosition +
					(dampenedDeltaX / window.innerWidth) * 100 * velocity * 0.5; // Riduci anche la velocità
				setSwipePosition(dampenedNewPosition);
				return; // Interrompi l'aggiornamento della tab attiva
			}

			setSwipePosition(newPotentialPosition);
			if (
				newPotentialTab !== activeTab &&
				newPotentialTab >= 0 &&
				newPotentialTab < tabs.length
			) {
				setActiveTab(newPotentialTab);
			}
		};

		const handleSwipeEnd = () => {
			console.log("handleSwipeEnd", activeTab);
			const activeTabPosition = activeTab * -100;

			setSwipePosition(activeTabPosition);
			setLastSwipePosition(activeTabPosition);
		};

		const swipeHandlers = useSwipeable({
			onSwiping: (eventData) => handleSwipe(eventData),
			onSwiped: () => handleSwipeEnd(),
			trackMouse: true, // Aggiungi questa opzione per abilitare lo swipe con il mouse
		});

		useEffect(() => {
			if (activeTabRef.current) {
				const width = activeTabRef.current.getLabelWidth();
				const left = activeTabRef.current.getLabelStart();

				indicatorRef.current.style.width = `${width}px`;
				indicatorRef.current.style.left = `${left}px`;
			}
		}, [activeTab]);

		window.addEventListener("resize", () => {
			if (activeTabRef.current) {
				const width = activeTabRef.current.getLabelWidth();
				const left = activeTabRef.current.getLabelStart();

				indicatorRef.current.style.width = `${width}px`;
				indicatorRef.current.style.left = `${left}px`;
			}
		});

		if (isTopBar) {
			window.addEventListener("scroll", () => {
				const scrollTop = window.scrollY || 0;
				if (scrollTop >= 24 && !lifted) {
					console.log("lifted");

					setLifted(true);
				} else if (scrollTop < 24) {
					console.log("unlifted");

					setLifted(false);
				}
			});
		}

		useImperativeHandle(ref, () => ({
			setActivePosition: (index) => {
				setActiveTab(index);
				setSwipePosition(index * -100);
			},
		}));

		return (
			<TabLayoutContainer>
				<TabsContainer isTopBar={isTopBar} isLifted={lifted}>
					{tabs.map((tab, index) => (
						<Tab
							key={index}
							label={tab.label}
							icon={tab.icon}
							onClick={() => handleTabClick(index)}
							isActive={index === activeTab}
							ref={index === activeTab ? activeTabRef : null}
						/>
					))}
					<TabIndicator
						ref={indicatorRef}
						tabsLength={tabs.length}
						activeTab={activeTab}
					/>
				</TabsContainer>
				<ViewPagerContainer ref={viewPagerRef} {...swipeHandlers}>
					{tabs.map((tab, index) => (
						<ViewPager
							key={index}
							style={{
								transform: `translateX(${swipePosition}%)`,
							}}
						>
							{tab.content}
						</ViewPager>
					))}
				</ViewPagerContainer>
			</TabLayoutContainer>
		);
	}
);

export default TabLayout;
